import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router';
import type { HypothesisConfig, HypothesisSummary } from '../api/useHypotheses.ts';
import { Ikon } from '../status/Ikon.tsx';
import { hypoteseOppforing, hypoteseStatusNoekkel, tokenVar } from '../status/status.ts';
import { nummerTekst } from './Kvittering.tsx';
import { dagerTil, fornavn, hvem, MAKS_TRENGER_MER, navnAv, statusNavn } from './lesning.ts';
import { kortDato, kortDatoAv, retningOrd, tall } from './setning.ts';

/**
 * Raden i innboksen og «Til deg» (innboks-konseptets rad, DD-67; `HypRad` i
 * `admin-hypoteser.jsx`): statusens ikon i statusens farge i egen kolonne, tittelen i body
 * semibold, ETT tall til høyre, og ÉN meta-linje i small — status halvfet først, så det som
 * gjelder, så nummeret. Under 900 px er meta-linjen status og bare det som avviker (avvik 45).
 *
 * Tallet til høyre: «Avgjør nå» og «Forsinket» i warning med ikon (DD-53, aldri rødt), ellers
 * datoen som gjelder — fristen halvfet i Vurderes, datoen Vurderes VILLE gitt uten halvfet i
 * Mottatt (DD-28: ingen dato er lovet før noen har tatt den).
 */

interface Ledd {
  tekst: string;
  varsel?: boolean;
  halvfet?: boolean;
  /** Et merke med eget ikon, som «notis til brukerne» (H3.1). */
  ikon?: string;
}

/** H3.1: notisen venter på den som skriver den — merket står så lenge, og forsvinner når den er avgjort. */
const NOTIS_LEDD: Ledd = { tekst: 'notis til brukerne', ikon: 'megaphone-outline' };

export function radLedd(h: HypothesisSummary, megId: string | undefined): { meta: Ledd[]; avvik: Ledd[] } {
  const meta: Ledd[] = [];
  const avvik: Ledd[] = [];
  const stille: Ledd | null = h.inactiveDays >= 28 && (h.statusType === 'reviewing' || h.statusType === 'active') ? { tekst: '4 uker uten aktivitet', varsel: true } : null;
  if (h.conclusionNoticeDue) {
    // Skjerm 11: «Delvis bekreftet · konkludert 7. des · notis til brukerne · H-142».
    meta.push({ tekst: `${h.status === 'stoppet' ? 'stoppet' : 'konkludert'} ${kortDatoAv(h.statusChangedAt)}` }, NOTIS_LEDD);
    avvik.push(NOTIS_LEDD);
  } else if (h.awaitingSubmitter) {
    meta.push({ tekst: `venter på innsender · ${h.needsMoreCount} av ${MAKS_TRENGER_MER}`, halvfet: true });
    avvik.push({ tekst: 'venter på innsender', halvfet: true });
  } else if (h.status === 'mottatt') {
    if (h.submittedAt) meta.push({ tekst: kortDatoAv(h.submittedAt) });
  } else if (h.status === 'valgt' || h.status === 'testes' || h.status === 'pa_pause') {
    if (h.owner) meta.push({ tekst: `${hvem(h.owner, megId, 'du')} eier` });
  } else if (h.reviewer) {
    meta.push({ tekst: hvem(h.reviewer, megId, 'du') === 'du' ? 'du vurderer' : navnAv(h.reviewer) });
  }
  if (stille) {
    meta.push(stille);
    avvik.push(stille);
  }
  if (h.status === 'mottatt' || h.status === 'vurderes') meta.push({ tekst: fraInnsenderne(h, megId) });
  // H3.3 (W11): «bygger på H-88» (innsenderens «Min er en annen») og «ligner på H-143» (veien til
  // sammenslåing) — merker med eget ikon, så rad-regelen holder (maks sju elementer).
  if (h.supersedes) {
    const l: Ledd = { tekst: `bygger på ${nummerTekst(h.supersedes.number)}`, ikon: 'list-outline' };
    meta.push(l);
    avvik.push(l);
  }
  if (h.mergeCandidate) {
    const l: Ledd = { tekst: `ligner på ${nummerTekst(h.mergeCandidate.number)}`, ikon: 'git-merge-outline' };
    meta.push(l);
    avvik.push(l);
  }
  meta.push({ tekst: nummerTekst(h.number) });
  return { meta, avvik };
}

/** «fra Emil Strand og Mia Holm» — innsenderen og medinnsenderne fra sammenslåinger (H3.3, W13). */
export function fraInnsenderne(h: HypothesisSummary, megId: string | undefined): string {
  const navn = [h.submittedBy, ...(h.coSubmitters ?? []).map((c) => c.admin)].map((r) => (r.id === megId ? 'deg' : navnAv(r)));
  const unike = [...new Set(navn)];
  return `fra ${unike.length <= 1 ? unike[0] : `${unike.slice(0, -1).join(', ')} og ${unike[unike.length - 1]}`}`;
}

/**
 * Registerets rad (W9, M10b; `hypotese-innsending.jsx` RADER): samme rad som innboksens, men
 * tallet til høyre er datoen som gjelder for ALLE statuser — fristen halvfet der den er et løfte
 * (Vurderes) eller en dato hypotesen tas opp igjen (Parkert), ellers datoen den kom dit — og
 * meta-linjen navngir den som har hypotesen nå. Innsenderen står i detaljen.
 *
 * De konkluderte har resultatet med terskelen til høyre (`result`, W9): «18 % → **27 %** · rett
 * hvis minst 22 %», og datoen flytter til meta-linjen («konkludert 3. sep»). Under 900 holder
 * tallet én linje — «18 → **27 %**», enheten én gang — og terskelen står i meta-linjen (M10b).
 * Uten målt baseline står bare resultatet. Stoppet og avvist har kodens etikett fra rommets
 * ordlyd (`stopReasonWording`, `rejectReasonWording`) — aldri koden — avvist før
 * beslutningstakerens navn.
 */
export interface Resultatlinje {
  /** Baselinen med enheten (over 900) og uten (under), eller null når den ikke var målt. */
  baseline: { bred: string; smal: string } | null;
  /** Resultatet med enheten — det halvfete. */
  resultat: string;
  /** «rett hvis minst 22 %». */
  terskel: string;
}

/** Hele linjen som tekst, som over 900 px: «18 % → 27 % · rett hvis minst 22 %». */
export const resultatTekst = (r: Resultatlinje): string => `${r.baseline ? `${r.baseline.bred} → ` : ''}${r.resultat} · ${r.terskel}`;

export function resultatlinje(h: HypothesisSummary): Resultatlinje | null {
  const r = h.result;
  if (h.status !== 'konkludert' || !r) return null;
  const enhet = (v: number) => `${tall(v)} ${r.unit}`.trim();
  return {
    baseline: r.baselineValue != null ? { bred: enhet(r.baselineValue), smal: tall(r.baselineValue) } : null,
    resultat: enhet(r.resultValue),
    terskel: `rett hvis ${retningOrd(r.direction)} ${enhet(r.threshold)}`,
  };
}

export function registerLedd(h: HypothesisSummary, megId: string | undefined, config: HypothesisConfig | undefined): { hoyre: { foer: string; dato: string; fet?: boolean } | null; resultat: Resultatlinje | null; meta: Ledd[]; avvik: Ledd[] } {
  const meta: Ledd[] = [];
  const avvik: Ledd[] = [];
  const navn = (ref: HypothesisSummary['owner']) => navnAv(ref);
  const egen = (ref: HypothesisSummary['owner']) => Boolean(ref && ref.id === megId);
  const eier = (): Ledd => (h.owner ? { tekst: egen(h.owner) ? 'du eier' : `eier ${navn(h.owner)}` } : { tekst: 'venter på eier' });
  const avgjoer = (): Ledd[] => (h.decider ? [{ tekst: egen(h.decider) ? 'du avgjør' : `${navn(h.decider)} avgjør` }] : []);
  const vurderer = (): Ledd[] => (h.reviewer ? [{ tekst: egen(h.reviewer) ? 'du vurderer' : navn(h.reviewer) }] : []);
  const stille: Ledd | null = h.inactiveDays >= 28 && (h.statusType === 'reviewing' || h.statusType === 'active') ? { tekst: '4 uker uten aktivitet', varsel: true } : null;
  let hoyre: { foer: string; dato: string; fet?: boolean } | null = null;
  const resultat = resultatlinje(h);
  const da = (iso: string | undefined) => (iso ? kortDatoAv(iso) : '');
  switch (h.status) {
    case 'mottatt':
      if (h.projectedDeadlineAt) hoyre = { foer: 'innen', dato: `${da(h.projectedDeadlineAt)} når den tas` };
      break;
    case 'vurderes':
      if (h.deadlineAt) hoyre = { foer: 'avgjørelse innen', dato: da(h.deadlineAt), fet: true };
      if (h.awaitingSubmitter) {
        meta.push({ tekst: 'venter på innsender', halvfet: true });
        avvik.push({ tekst: 'venter på innsender', halvfet: true });
      } else meta.push(...vurderer());
      break;
    case 'parkert':
      if (h.revisitAt) hoyre = { foer: 'tas opp igjen', dato: kortDato(h.revisitAt), fet: true };
      meta.push(...vurderer());
      avvik.push({ tekst: 'tas opp igjen' });
      break;
    case 'pa_pause':
      if (h.revisitAt) hoyre = { foer: 'pause til', dato: kortDato(h.revisitAt), fet: true };
      meta.push(eier(), ...avgjoer());
      avvik.push(eier());
      break;
    case 'valgt':
      hoyre = { foer: 'valgt', dato: da(h.statusChangedAt) };
      meta.push(eier(), ...avgjoer());
      avvik.push(eier());
      break;
    case 'testes':
      if (h.conclusionDueAt) hoyre = { foer: 'konklusjon innen', dato: da(h.conclusionDueAt) };
      meta.push(eier(), ...avgjoer());
      avvik.push(eier());
      break;
    case 'konkludert':
      if (resultat) {
        // W9: «Bekreftet · konkludert 3. sep · H-61»; M10b: «Bekreftet · rett hvis minst 22 %».
        if (h.statusChangedAt) meta.push({ tekst: `konkludert ${da(h.statusChangedAt)}` });
        avvik.push({ tekst: resultat.terskel });
        break;
      }
      hoyre = { foer: 'konkludert', dato: da(h.statusChangedAt) };
      if (h.owner) {
        meta.push(eier());
        avvik.push(eier());
      }
      break;
    case 'stoppet': {
      hoyre = { foer: statusNavn(config, h).toLowerCase(), dato: da(h.statusChangedAt) };
      // «Stoppet · Prioriteringen endret seg · H-72» (W9). Uten kode: den som avgjorde.
      const kode = h.stopReason ? config?.stopReasonWording.find((w) => w.code === h.stopReason)?.label : undefined;
      if (kode) {
        meta.push({ tekst: kode });
        avvik.push({ tekst: kode });
      } else meta.push(...(h.decider ? [{ tekst: navn(h.decider) }] : vurderer()));
      break;
    }
    case 'avvist': {
      hoyre = { foer: statusNavn(config, h).toLowerCase(), dato: da(h.statusChangedAt) };
      // «Avvist · For stor · Rune Aas · H-143» (W9): koden før navnet.
      const kode = h.rejectReason ? config?.rejectReasonWording.find((w) => w.code === h.rejectReason)?.label : undefined;
      if (kode) {
        meta.push({ tekst: kode });
        avvik.push({ tekst: kode });
      }
      meta.push(...(h.decider ? [{ tekst: navn(h.decider) }] : vurderer()));
      break;
    }
    case 'trukket':
      hoyre = { foer: statusNavn(config, h).toLowerCase(), dato: da(h.statusChangedAt) };
      break;
    case 'slaatt_sammen':
      // H3.3: «Slått sammen · følges videre i H-143» (W13, Mine hypoteser).
      hoyre = { foer: statusNavn(config, h).toLowerCase(), dato: da(h.statusChangedAt) };
      if (h.mergedInto) {
        meta.push({ tekst: `følges videre i ${nummerTekst(h.mergedInto.number)}`, ikon: 'git-merge-outline' });
        avvik.push({ tekst: `følges videre i ${nummerTekst(h.mergedInto.number)}` });
      }
      break;
    default:
      break;
  }
  if (stille) {
    meta.push(stille);
    avvik.push(stille);
  }
  // H2.8 (DD-80): kilden, aldri hvem som la den inn — det er detaljens.
  if (h.importSource) meta.push({ tekst: `fra ${h.importSource}` });
  meta.push({ tekst: nummerTekst(h.number) });
  return { hoyre, resultat, meta, avvik };
}

/**
 * «Mine hypoteser» (W7; `hypotese-innsending.jsx` RADER): registerets rad sett fra innsenderen.
 * Tallet til høyre er det samme, men meta-linjen sier «venter på deg» (halvfet) der registeret
 * sier «venter på innsender», og et avslag har bare koden — navnet står i hypotesen (W7: «Avvist ·
 * For stor · H-143»).
 */
export function mineLedd(h: HypothesisSummary, megId: string | undefined, config: HypothesisConfig | undefined): ReturnType<typeof registerLedd> {
  const r = registerLedd(h, megId, config);
  const deg = (l: Ledd): Ledd => (l.tekst === 'venter på innsender' ? { tekst: 'venter på deg', halvfet: true } : l);
  const navn = h.decider ? navnAv(h.decider) : h.reviewer ? navnAv(h.reviewer) : undefined;
  const utenNavn = (ledd: Ledd[]) => (h.status === 'avvist' ? ledd.filter((l) => l.tekst !== navn) : ledd);
  return { ...r, meta: utenNavn(r.meta.map(deg)), avvik: utenNavn(r.avvik.map(deg)) };
}

function RegisterHoyre({ h, megId, config, smal, mine = false }: { h: HypothesisSummary; megId: string | undefined; config: HypothesisConfig | undefined; smal: boolean; mine?: boolean }) {
  // «Avgjør nå» og «Forsinket» er de samme varslene som i innboksen (DD-53).
  if (h.deadlineState === 'avgjoer_naa' || h.deadlineState === 'forsinket') return <Hoyre h={h} />;
  const { hoyre: v, resultat: r } = (mine ? mineLedd : registerLedd)(h, megId, config);
  if (r) {
    // Under 900: «18 → 27 %», enheten én gang og terskelen i meta-linjen (M10b).
    if (smal) {
      return (
        <span className="hb-rad-hoyre">
          {r.baseline ? `${r.baseline.smal} →` : null} <b className="hb-rad-fet">{r.resultat}</b>
        </span>
      );
    }
    return (
      <span className="hb-rad-hoyre">
        {r.baseline ? `${r.baseline.bred} →` : null} <b className="hb-rad-fet">{r.resultat}</b> · {r.terskel}
      </span>
    );
  }
  if (!v) return <span className="hb-rad-hoyre" />;
  // Under 900 holder tallet én linje: bare datoen (M10b).
  if (smal) return <span className={v.fet ? 'hb-rad-hoyre hb-rad-fet' : 'hb-rad-hoyre'}>{v.dato.replace(/ når den tas$/, '')}</span>;
  return (
    <span className="hb-rad-hoyre">
      {v.foer} {v.fet ? <b className="hb-rad-fet">{v.dato}</b> : v.dato}
    </span>
  );
}

function Hoyre({ h }: { h: HypothesisSummary }) {
  if (h.deadlineState === 'avgjoer_naa') {
    return (
      <span className="hb-rad-hoyre hb-varsel">
        <Ikon navn="warning-outline" />
        Avgjør nå
      </span>
    );
  }
  if (h.deadlineState === 'forsinket') {
    const frist = h.status === 'parkert' || h.status === 'pa_pause' ? h.revisitAt : (h.deadlineAt ?? h.conclusionDueAt);
    const dager = frist ? -dagerTil(frist.length === 10 ? `${frist}T12:00:00Z` : frist) : 0;
    return (
      <span className="hb-rad-hoyre hb-varsel">
        <Ikon navn="warning-outline" />
        Forsinket {dager > 0 ? `${dager} d` : ''}
      </span>
    );
  }
  if (h.status === 'vurderes' && h.deadlineAt) return <span className="hb-rad-hoyre hb-rad-fet">{kortDatoAv(h.deadlineAt)}</span>;
  if (h.status === 'mottatt' && h.projectedDeadlineAt) return <span className="hb-rad-hoyre">{kortDatoAv(h.projectedDeadlineAt)}</span>;
  if ((h.status === 'parkert' || h.status === 'pa_pause') && h.revisitAt) return <span className="hb-rad-hoyre">{kortDato(h.revisitAt)}</span>;
  if (h.status === 'testes' && h.conclusionDueAt) return <span className="hb-rad-hoyre">{kortDatoAv(h.conclusionDueAt)}</span>;
  return <span className="hb-rad-hoyre" />;
}

/**
 * `til` gjør raden til en lenke (Mine hypoteser, W7: hypotesen er en egen side, ikke en detalj ved
 * siden av), og `mine` gir innsenderens meta-linje. Uten `til` er raden en knapp som velger i lista.
 */
export function HypRad({ h, config, megId, valgt, smal, register = false, mine = false, til, venter, faktor, onVelg }: { h: HypothesisSummary; config: HypothesisConfig | undefined; megId: string | undefined; valgt: boolean; smal: boolean; register?: boolean; mine?: boolean; til?: string; /** H3.1: hvor mange notisen venter på — radens tall i «Til deg». */ venter?: number; /** H3.2: faktoren registeret er sortert på — radens tall til høyre (svar 4). */ faktor?: 'impact' | 'confidence' | 'effort'; onVelg?: () => void }) {
  const noekkel = hypoteseStatusNoekkel(h.status, h.outcome);
  const o = hypoteseOppforing(noekkel);
  const navn = statusNavn(config, h);
  const { meta, avvik } = mine ? mineLedd(h, megId, config) : register ? registerLedd(h, megId, config) : radLedd(h, megId);
  // Skjermleseren får resultatlinjen som øyet ser den over 900 px, uansett bredde.
  const r = register || mine ? resultatlinje(h) : null;
  const resultat = r ? ` ${resultatTekst(r).replace(' · ', ', ')}.` : '';
  const synlige = smal ? avvik : meta;
  const farge = { '--hb-status': tokenVar(o.fg) } as CSSProperties;
  const innhold = (
    <>
      <Ikon navn={o.ikon} className="hb-rad-ikon" />
      <span className="hb-rad-tittel">{h.title}</span>
      {faktor ? (
        <span className="hb-rad-hoyre" data-testid="hb-rad-faktor">
          {FAKTOR[faktor]} <b className="hb-rad-fet">{h.score?.[faktor] !== undefined ? faktorVerdi(faktor, h.score[faktor]!) : '—'}</b>
        </span>
      ) : venter !== undefined && h.conclusionNoticeDue ? (
        <span className="hb-rad-hoyre">
          <b className="hb-rad-fet">{venter}</b> venter
        </span>
      ) : register || mine ? (
        <RegisterHoyre h={h} megId={megId} config={config} smal={smal} mine={mine} />
      ) : (
        <Hoyre h={h} />
      )}
      <span className="hb-rad-meta">
        <b className="hb-rad-status">{navn}</b>
        {synlige.map((l) => (
          <span key={l.tekst} className={l.varsel ? 'hb-rad-ledd hb-varsel' : l.halvfet ? 'hb-rad-ledd hb-rad-fet' : 'hb-rad-ledd'}>
            <span aria-hidden="true" className="hb-prikk">
              ·
            </span>
            {l.varsel ? <Ikon navn="warning-outline" /> : l.ikon ? <Ikon navn={l.ikon} className="hb-rad-merkeikon" /> : null}
            {l.tekst}
          </span>
        ))}
      </span>
    </>
  );
  const felles = {
    className: valgt ? 'hb-rad hb-rad-valgt' : 'hb-rad',
    'aria-current': valgt ? ('true' as const) : undefined,
    'aria-label': `${nummerTekst(h.number)}: ${h.title ?? ''}. ${navn}${meta.length > 1 ? `, ${meta.slice(0, -1).map((l) => l.tekst).join(', ')}` : ''}.${resultat}`,
    style: farge,
    'data-testid': `hb-rad-${h.number ?? h.id}`,
  };
  return (
    <li>
      {til ? (
        <Link to={til} {...felles} data-mine-rad="">
          {innhold}
        </Link>
      ) : (
        <button type="button" {...felles} onClick={onVelg}>
          {innhold}
        </button>
      )}
    </li>
  );
}

const FAKTOR = { impact: 'Impact', confidence: 'Confidence', effort: 'Effort' } as const;
/** Faktoren som skalaen skriver den: ½ og ¼ for Impact, prosent for Confidence, ukesverk for Effort. */
const faktorVerdi = (f: keyof typeof FAKTOR, v: number): string => (f === 'impact' ? (v === 0.5 ? '½' : v === 0.25 ? '¼' : tall(v)) : f === 'confidence' ? `${v} %` : `${tall(v)} uv`);

/** Gruppetittelen i «Til deg»: navnet og antallet. */
export function Gruppetittel({ children }: { children: ReactNode }) {
  return <li className="hb-gruppe">{children}</li>;
}

/** Skjelett mens lista laster: raden i grått, aldri en spinner over hele flaten. */
export function Skjelettrader({ etikett }: { etikett: string }) {
  return (
    <div role="status" aria-busy="true" aria-label={etikett} className="panel-skjelett">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="panel-skjelett-rad">
          <span className="panel-skjelett-linje" />
          <span className="panel-skjelett-linje panel-skjelett-kort" />
        </div>
      ))}
    </div>
  );
}

/** Fornavnet på innsenderen, for setningene i foten og toasten. */
export const innsenderFornavn = (h: HypothesisSummary) => fornavn(h.submittedBy);
